/**
 * Multi-pot allocation dialog for an investment line.
 *
 * Lets the user split a single budget detail line across one or more
 * strategic pots — including the synthetic Overflow pot. The sum of every
 * allocation must equal the line's `estimatedCostExclVat`. The BE caps each
 * non-null pot at its current `remainingAmount` and tips any leftover into
 * the overflow slice automatically (see `normalizeAllocations` server-side),
 * but we surface the same headroom hints here so the user knows what fits.
 *
 * Persists with PUT /investment/items/:id and a body of
 * `{ bucketAllocations: [{ bucketId, amount }] }`.
 */

import { useEffect, useMemo, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import {
  Wallet,
  Warning as WarningIcon,
  Plus,
  Trash,
  ArrowsCounterClockwise,
} from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InvestmentItem } from "screens/investment/types/investment";
import {
  useBudgetBuckets,
  dispatchBudgetBucketsChanged,
} from "screens/investment/hooks/useBudgetBuckets";

interface BucketAllocationDialogProps {
  item: InvestmentItem | null;
  isOpen: boolean;
  onClose: () => void;
  /** Called once the BE confirms the new allocations. */
  onSaved: () => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

interface Draft {
  // null bucketId means the overflow slice
  bucketId: number | null;
  amount: number;
}

export const BucketAllocationDialog = ({
  item,
  isOpen,
  onClose,
  onSaved,
}: BucketAllocationDialogProps) => {
  const { session } = useSession();
  const buildingId = item?.buildingId ?? item?.building?.id ?? undefined;
  const { buckets } = useBudgetBuckets(buildingId);

  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Real (non-overflow) pots first, then a synthetic overflow choice. The
  // overflow chip is always offered so users can deliberately park a slice
  // there without first creating a real pot.
  const realBuckets = useMemo(
    () => buckets.filter((b) => !b.isOverflow),
    [buckets],
  );

  // Reset drafts every time the dialog opens with a fresh line. Only real
  // strategic pots are pickable — overflow / onvoorzien is *never* an
  // explicit slice the user can budget against. Overflow appears purely as
  // a derived consequence of project-part spending exceeding the line's
  // strategic intent (handled at display time on the table).
  //
  // The legacy `bucketAllocations` rows can drift out of sync with the
  // line's `estimatedCostExclVat` (e.g. user raised the line budget via
  // the changeBudget flow which doesn't always rewrite the allocation
  // amounts to match). When that happens we prefer the line's actual
  // current cost as the source of truth — otherwise the dialog opens
  // showing the old slot size and the user can't tell why.
  useEffect(() => {
    if (!isOpen || !item) return;
    const lineCost = Number(item.estimatedCostExclVat) || 0;
    const realAllocations =
      item.bucketAllocations?.filter((a) => a.bucket?.id) ?? [];
    const allocationsSum = realAllocations.reduce(
      (s, a) => s + (Number(a.amount) || 0),
      0,
    );

    let seeded: Draft[];
    // Use the persisted multi-pot allocations only when their sum matches
    // the line cost. A €1 tolerance covers rounding noise.
    if (
      realAllocations.length > 0 &&
      Math.abs(allocationsSum - lineCost) <= 1
    ) {
      seeded = realAllocations.map((a) => ({
        bucketId: a.bucket!.id,
        amount: Number(a.amount) || 0,
      }));
    } else if (item.bucket?.id) {
      // Fall back to a single allocation reflecting the line's *current*
      // cost on its primary bucket — covers both legacy single-pot lines
      // and lines whose multi-pot rows fell out of sync.
      seeded = [{ bucketId: item.bucket.id, amount: lineCost }];
    } else {
      seeded = [];
    }
    setDrafts(seeded);
  }, [isOpen, item?.id, item?.estimatedCostExclVat]);

  if (!item) return null;

  // Original line amount — kept around purely as the "vorig totaal" hint so
  // users can see how much they're growing/shrinking the line.
  const originalTotal = Number(item.estimatedCostExclVat) || 0;
  // The new total is derived directly from the allocation sum: dragging more
  // budget out of a pot grows the line, removing slices shrinks it.
  const newTotal = drafts.reduce((s, d) => s + (Number(d.amount) || 0), 0);
  const delta = newTotal - originalTotal;

  const setDraftAmount = (idx: number, amount: number) => {
    setDrafts((prev) => {
      const target = prev[idx];
      if (!target) return prev;
      // Hard cap at the pot's headroom — the dialog only manages strategic
      // budget. Anything beyond the pot's capacity is project-part overflow
      // and is recorded as such elsewhere, not as a budget allocation here.
      //
      // Headroom = bucket.totalAmount − (claims of OTHER lines on this pot).
      // We derive the line's own current claim on the bucket from its actual
      // current cost (capped at bucket capacity) rather than from the
      // possibly-stale `bucketAllocations` row, so a user who just raised
      // their line's `estimatedCostExclVat` can re-allocate up to the full
      // available capacity instead of being stuck at the old slot size.
      if (target.bucketId != null) {
        const bucket = buckets.find((b) => b.id === target.bucketId);
        if (bucket && !bucket.isOverflow) {
          const lineCost = Number(item?.estimatedCostExclVat) || 0;
          const bucketTotal = Number(bucket.totalAmount) || 0;
          const lineOwnClaim =
            item?.bucket?.id === bucket.id
              ? Math.min(lineCost, bucketTotal || lineCost)
              : Number(
                  item?.bucketAllocations?.find(
                    (a) => a.bucket?.id === bucket.id,
                  )?.amount ?? 0,
                );
          const otherLinesClaim = Math.max(
            0,
            (Number(bucket.usedAmount) || 0) - lineOwnClaim,
          );
          const headroom = Math.max(0, bucketTotal - otherLinesClaim);
          const capped = Math.min(amount, headroom);
          return prev.map((d, i) =>
            i === idx ? { ...d, amount: capped } : d,
          );
        }
      }
      return prev.map((d, i) => (i === idx ? { ...d, amount } : d));
    });
  };

  const removeDraft = (idx: number) => {
    setDrafts((prev) => prev.filter((_, i) => i !== idx));
  };

  const togglePot = (bucketId: number | null) => {
    // Overflow / onvoorzien is never a pickable strategic pot — guard against
    // any caller that still tries.
    if (bucketId == null) return;
    setDrafts((prev) => {
      const idx = prev.findIndex((d) => d.bucketId === bucketId);
      if (idx >= 0) {
        return prev.filter((_, i) => i !== idx);
      }
      // Adding a new pot doesn't reshuffle existing amounts — the new pot
      // grows the line by whatever amount the user types. Seed at 0.
      return [...prev, { bucketId, amount: 0 }];
    });
  };

  const splitEvenly = () => {
    if (drafts.length === 0 || originalTotal <= 0) return;
    const each = Math.floor(originalTotal / drafts.length);
    setDrafts((prev) =>
      prev.map((d, i) => ({
        ...d,
        amount:
          i === prev.length - 1
            ? originalTotal - each * (prev.length - 1)
            : each,
      })),
    );
  };

  /**
   * "Vul rest" on a row sets that row's amount to the pot's full available
   * headroom — i.e. consume all the budget that pot still has. This grows
   * the line if needed; the user no longer has a fixed total to fit into.
   *
   * Same headroom math as `setDraftAmount`: derive the line's own claim
   * from `estimatedCostExclVat` (when this is the primary bucket) rather
   * than the stale `bucketAllocations` row, so a freshly-raised line gets
   * the full available capacity instead of the old slot size.
   */
  const fillRest = (idx: number) => {
    setDrafts((prev) =>
      prev.map((d, i) => {
        if (i !== idx) return d;
        if (d.bucketId === null) {
          // Overflow has no cap — leave the user's typed value.
          return d;
        }
        const bucket = buckets.find((b) => b.id === d.bucketId);
        if (!bucket) return d;
        const lineCost = Number(item.estimatedCostExclVat) || 0;
        const bucketTotal = Number(bucket.totalAmount) || 0;
        const lineOwnClaim =
          item.bucket?.id === bucket.id
            ? Math.min(lineCost, bucketTotal || lineCost)
            : Number(
                item.bucketAllocations?.find((a) => a.bucket?.id === bucket.id)
                  ?.amount ?? 0,
              );
        const otherLinesClaim = Math.max(
          0,
          (Number(bucket.usedAmount) || 0) - lineOwnClaim,
        );
        const headroom = Math.max(0, bucketTotal - otherLinesClaim);
        return { ...d, amount: headroom };
      }),
    );
  };

  const handleSave = async () => {
    if (!session || !item) return;
    if (newTotal <= 0) {
      toast.error("Wijs minstens één strategisch budget een bedrag toe");
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/investment/items/${item.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            // The line's total grows/shrinks to match the allocation sum.
            // Sending both keeps the BE consistent — `normalizeAllocations`
            // uses estimatedCostExclVat as the canonical line amount.
            estimatedCostExclVat: newTotal,
            bucketAllocations: drafts
              .filter((d) => Number(d.amount) > 0)
              .map((d) => ({
                bucketId: d.bucketId,
                amount: Number(d.amount) || 0,
              })),
          }),
        },
        session,
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || "Failed to update bucket allocations");
      }
      toast.success("Strategische budgetten bijgewerkt");
      onSaved();
      if (buildingId) dispatchBudgetBucketsChanged(buildingId);
      onClose();
    } catch (err) {
      console.error("[BucketAllocationDialog] save failed", err);
      toast.error(
        err instanceof Error
          ? err.message
          : "Fout bij bijwerken strategische budgetten",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Real strategic pots that aren't yet in drafts — these are the only
  // pickable additions (overflow / onvoorzien is never user-budgetable).
  const draftBucketIds = new Set(drafts.map((d) => d.bucketId));
  const availableToAdd = realBuckets.filter((b) => !draftBucketIds.has(b.id));

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !open && !isSubmitting && onClose()}
    >
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-moss-primary" weight="duotone" />
            Strategische budgetten — {item.title}
          </DialogTitle>
          <DialogDescription className="text-sm text-neutral-secondary-mid">
            Verdeel deze budgetlijn over één of meerdere strategische budgetten.
            Het strategisch budget per allocatie is gelimiteerd tot de capaciteit
            van het strategisch budget. Verwachte overflow ontstaat automatisch
            wanneer projectonderdelen meer gebruiken dan het strategisch
            budget.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-3 overflow-y-auto py-2 pr-1">
          {/* Totals: original line vs new line (= sum of allocations). */}
          <div className="rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2">
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-neutral-secondary-mid">
                Vorig lijntotaal
              </span>
              <span className="text-neutral-secondary-mid tabular-nums">
                {formatCurrency(originalTotal)}
              </span>
            </div>
            <div className="mt-1 flex items-center justify-between text-sm">
              <span className="text-neutral-primary font-medium">
                Nieuw lijntotaal
              </span>
              <span className="text-neutral-primary font-semibold tabular-nums">
                {formatCurrency(newTotal)}
                {Math.abs(delta) > 0.5 && (
                  <span
                    className={`ml-2 text-[11px] font-medium ${
                      delta > 0 ? "text-moss-primary" : "text-rose-600"
                    }`}
                  >
                    ({delta > 0 ? "+" : "−"}
                    {formatCurrency(Math.abs(delta))})
                  </span>
                )}
              </span>
            </div>
          </div>

          {/* Active allocations */}
          <div className="space-y-2">
            {drafts.length === 0 && (
              <div className="text-neutral-secondary-mid rounded-md border border-dashed border-neutral-200 px-3 py-6 text-center text-xs italic">
                Nog geen strategisch budget geselecteerd. Voeg er hieronder eentje toe.
              </div>
            )}
            {drafts.map((draft, idx) => {
              const bucket =
                draft.bucketId === null
                  ? null
                  : buckets.find((b) => b.id === draft.bucketId) || null;
              const isOverflow = draft.bucketId === null;
              const headroom = bucket
                ? Number(bucket.remainingAmount) || 0
                : null;
              return (
                <div
                  key={`${draft.bucketId ?? "overflow"}-${idx}`}
                  className={`flex items-center gap-2 rounded-md border px-3 py-2 ${
                    isOverflow
                      ? "border-amber-300 bg-amber-50/60"
                      : "border-moss-primary/30 bg-moss-primary/5"
                  }`}
                >
                  <div className="flex flex-1 flex-col gap-0.5">
                    <div className="flex items-center gap-1.5 text-sm font-medium">
                      {isOverflow ? (
                        <>
                          <WarningIcon
                            className="h-4 w-4 text-amber-700"
                            weight="fill"
                          />
                          <span className="text-amber-800">
                            Verwachte overflow
                          </span>
                        </>
                      ) : (
                        <>
                          <Wallet
                            className="h-4 w-4 text-moss-primary"
                            weight="duotone"
                          />
                          <span className="text-moss-primary">
                            {bucket?.name ?? `Pot #${draft.bucketId}`}
                          </span>
                        </>
                      )}
                    </div>
                    {!isOverflow && (
                      <div className="text-[10px] text-neutral-secondary-mid">
                        Beschikbaar:{" "}
                        <span className="tabular-nums">
                          {headroom !== null
                            ? formatCurrency(headroom)
                            : "—"}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1">
                    <span className="text-xs text-neutral-secondary-mid">
                      €
                    </span>
                    <Input
                      type="number"
                      min={0}
                      step={100}
                      value={draft.amount || ""}
                      onChange={(e) =>
                        setDraftAmount(idx, Number(e.target.value) || 0)
                      }
                      placeholder="0"
                      className="h-7 w-32 text-right text-sm tabular-nums"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => fillRest(idx)}
                      className="h-7 px-2 text-[10px]"
                      title="Vul met het restbedrag van de lijn"
                    >
                      Vul rest
                    </Button>
                    <button
                      type="button"
                      onClick={() => removeDraft(idx)}
                      className="rounded p-1 text-neutral-400 hover:bg-rose-50 hover:text-rose-600"
                      title="Verwijder strategisch budget uit verdeling"
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Add another pot — only real strategic pots are pickable. */}
          {availableToAdd.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-neutral-secondary-mid text-[10px] font-semibold uppercase tracking-wide">
                Pot toevoegen
              </div>
              <div className="flex flex-wrap gap-1.5">
                {availableToAdd.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => togglePot(b.id)}
                    className="inline-flex items-center gap-1 rounded-full border border-moss-primary/30 bg-white px-2 py-0.5 text-[11px] font-medium text-moss-primary hover:bg-moss-primary/5"
                    title={`${b.name} — beschikbaar ${formatCurrency(
                      Number(b.remainingAmount) || 0,
                    )}`}
                  >
                    <Plus size={10} />
                    <Wallet size={10} weight="duotone" />
                    {b.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quick actions */}
          {drafts.length > 1 && (
            <div className="flex gap-2 pt-1">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={splitEvenly}
                className="h-7 text-xs"
                title="Verdeel het oorspronkelijke lijntotaal gelijk over alle potten"
              >
                <ArrowsCounterClockwise size={12} className="mr-1" />
                Verdeel oorspronkelijk totaal gelijk
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Annuleren
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSubmitting || newTotal <= 0}
            className="bg-moss-primary text-white hover:bg-moss-primary/90"
          >
            {isSubmitting ? "Opslaan…" : "Opslaan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BucketAllocationDialog;
